import React, { useState } from "react"
import { Button, Select, Tag } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import { ShownNames, shownNames } from "../../model/shownLabels"
import {
  dimensionName,
  inSlice,
  legacySummary,
  partName,
  slicedRowName,
  summaryFits,
} from "../../model/summaries"
import { useTabSpecStore } from "../../runtime/tabSpecStore"
import type { CatalogueInstance, CatalogueQuestion } from "../../types/catalogue"
import type { SummaryChoice } from "../../types/tabSpecDocument"
import { summaryKey } from "../shared/SummaryCheckboxes.component"
import AddNetModal from "./AddNetModal.component"
import Field from "./Field.component"
import RowOrder from "./RowOrder.component"
import { useInspectedTable } from "./useInspectedTable"

/**
 * Everything a grid row can show, in the picker's order (`value`: the choice
 * as `summaryKey` writes it), with a summary of that kind to judge whether
 * it suits the question (`first`: the question's first answer).
 */
const PICKS: { value: string; label: string; sample: (first: string) => SummaryChoice }[] = [
  ...([1, 2, 3] as const).map((size) => ({
    value: `top-${size}`,
    label: `Top box ${size}`,
    sample: (): SummaryChoice => ({ kind: "top", size }),
  })),
  ...([1, 2, 3] as const).map((size) => ({
    value: `bottom-${size}`,
    label: `Bottom box ${size}`,
    sample: (): SummaryChoice => ({ kind: "bottom", size }),
  })),
  { value: "middle", label: "Middle", sample: () => ({ kind: "middle" }) },
  { value: "answer", label: "One answer", sample: (first) => ({ kind: "answer", label: first }) },
  {
    value: "net",
    label: "A net of answers",
    sample: (first) => ({ kind: "net", name: "Net", members: [first] }),
  },
  { value: "mean", label: "Mean", sample: () => ({ kind: "mean" }) },
  { value: "nps", label: "NPS", sample: () => ({ kind: "nps" }) },
  { value: "sum", label: "Sum", sample: () => ({ kind: "sum" }) },
]
/** The "Rows run along" choice of every instance in one list, unsliced. */
const ALL = "all"

/** The values of dimension `k` of `q`, in instance order, each once. */
const valuesOf = (q: CatalogueQuestion, k: number): string[] => [
  ...new Set(q.instances.flatMap((i) => (i.parts.length > k ? [i.parts[k]] : []))),
]

/**
 * A grid row of a summary as the tables show it: its own shown label, or
 * by its parts where the rows run when the table is fixed at some values
 * (`slicedRowName`).
 */
const rowName = (
  q: CatalogueQuestion,
  i: CatalogueInstance,
  slice: (string | null)[] | undefined,
  names: ShownNames
): string =>
  slice && q.dimensions.length > 1
    ? slicedRowName(q, i, slice, names)
    : names.instance(q.label, i.id, i.label || i.id)

/**
 * The rows of a grid summary: what each grid row shows (a box, the middle
 * or the mean of a scored question, one answer, a net of answers, NPS for a
 * 0 to 10 scale; the mean or the sum of numbers), along which dimension the
 * rows run for a question of several (the others fixed at one value each),
 * the grid rows, each of which can be hidden, and their order and whether
 * those nobody answered show (`RowOrder`). Everything reads as the tables
 * show it.
 */
const GridSummaryRows: React.FC<{ tableId: string }> = ({ tableId }) => {
  const inspected = useInspectedTable(tableId)
  const updateTable = useTabSpecStore((s) => s.updateTable)
  const clearOverrides = useTabSpecStore((s) => s.clearOverrides)
  const [netOpen, setNetOpen] = useState(false)
  if (!inspected) return null
  const { table, resolved, loaded } = inspected
  const names = shownNames(inspected.document.labels)
  const q = resolved.question
  if (!q)
    return (
      <p className="ts-hint">
        {loaded
          ? `${names.variable(table.question)} is no longer available.`
          : "The questionnaire is still loading."}
      </p>
    )
  const choice = resolved.summary ?? legacySummary(q, inspected.document.defaults)
  const optionName = (label: string): string => names.option(q.label, label)
  // What suits the question, by the rule Add table and the defaults follow
  // (`summaryFits`); what the table shows stays listed, suited or not.
  const first = q.options[0]?.label ?? ""
  const shown = summaryKey(choice)
  const picks = PICKS.filter(
    (p) => p.value === shown || summaryFits(p.sample(first), q, resolved.scores)
  ).map(({ value, label }) => ({ value, label }))
  const answers = q.options.map((o) => ({ value: o.label, label: optionName(o.label), title: o.label }))
  const writeSummary = (summary: SummaryChoice): void => updateTable(tableId, { summary })
  const pick = (value: string): void => {
    const [kind, size] = value.split("-")
    if (kind === "top" || kind === "bottom")
      writeSummary({ kind, size: Number(size) as 1 | 2 | 3 })
    else if (kind === "answer") {
      // The answer shown so far, or the first that is not an exception.
      const first = q.options.find((o) => !o.exception) ?? q.options[0]
      writeSummary({ kind: "answer", label: choice.kind === "answer" ? choice.label : (first?.label ?? "") })
    }
    else if (kind === "net") setNetOpen(true)
    else if (kind === "middle" || kind === "mean" || kind === "nps" || kind === "sum") writeSummary({ kind })
  }

  // Along which dimension the rows run, the others fixed (`slice`).
  const { slice } = table
  const several = q.dimensions.length > 1
  const runs = slice ? slice.indexOf(null) : -1
  const along = (value: string): void => {
    if (value === ALL) {
      clearOverrides(tableId, ["slice"])
      return
    }
    const k = Number(value)
    const next = q.dimensions.map((_, j) => (j === k ? null : (slice?.[j] ?? valuesOf(q, j)[0] ?? null)))
    writeSlice(next)
  }
  /** A new slice; the rows hidden outside it are hidden no more. */
  const writeSlice = (next: (string | null)[]): void => {
    const inside = new Set(q.instances.filter((i) => inSlice(i, next)).map((i) => i.id))
    const hidden = (table.hiddenRows ?? []).filter((id) => inside.has(id))
    updateTable(tableId, { slice: next })
    if (hidden.length === (table.hiddenRows ?? []).length) return
    if (hidden.length > 0) updateTable(tableId, { hiddenRows: hidden })
    else clearOverrides(tableId, ["hiddenRows"])
  }

  const rows = q.instances.filter((i) => inSlice(i, slice))
  const hidden = new Set(table.hiddenRows ?? [])
  const toggle = (id: string): void => {
    const next = rows.map((i) => i.id).filter((r) => (r === id ? !hidden.has(r) : hidden.has(r)))
    if (next.length === 0) clearOverrides(tableId, ["hiddenRows"])
    else updateTable(tableId, { hiddenRows: next })
  }

  return (
    <div className="ts-tab">
      <Field label="Each grid row shows">
        <Select
          size="small"
          aria-label="Each grid row shows"
          className="ts-field__select"
          value={shown}
          options={picks}
          // Eleven choices at most: every one is in the list.
          virtual={false}
          onChange={pick}
        />
        {choice.kind === "answer" && (
          <Select
            size="small"
            showSearch
            aria-label="Answer"
            className="ts-field__select"
            value={choice.label}
            options={answers}
            optionFilterProp="label"
            onChange={(label: string) => writeSummary({ kind: "answer", label })}
          />
        )}
        {choice.kind === "net" && (
          <p className="ts-row__members">
            <span className="ts-row__name">{choice.name}</span>
            {choice.members.map((m) => (
              <Tag key={m} className="ts-tag" title={m}>
                {optionName(m)}
              </Tag>
            ))}
            <Button type="link" size="small" className="ts-link" onClick={() => setNetOpen(true)}>
              Change the net
            </Button>
          </p>
        )}
      </Field>
      {several && (
        <Field label="Rows run along">
          <Select
            size="small"
            aria-label="Rows run along"
            className="ts-field__select"
            value={runs < 0 ? ALL : String(runs)}
            options={[
              ...q.dimensions.map((_, k) => ({ value: String(k), label: dimensionName(q, k, names) })),
              { value: ALL, label: "All in one list" },
            ]}
            onChange={along}
          />
          {slice &&
            slice.map((part, j) =>
              part === null ? null : (
                <div key={j} className="ts-field__row">
                  <span>{dimensionName(q, j, names)}</span>
                  <Select
                    size="small"
                    aria-label={`Fixed ${dimensionName(q, j, names)}`}
                    className="ts-field__select"
                    value={part}
                    options={valuesOf(q, j).map((v) => ({ value: v, label: partName(q, j, v, names) }))}
                    onChange={(v: string) => writeSlice(slice.map((p, i) => (i === j ? v : p)))}
                  />
                </div>
              )
            )}
        </Field>
      )}
      <Field label="Grid rows">
        <ol className="ts-rows" aria-label="Grid rows of the summary">
          {rows.map((i) => {
            const name = rowName(q, i, slice, names)
            const off = hidden.has(i.id)
            return (
              <li key={i.id} className={`ts-row${off ? " ts-row--hidden" : ""}`}>
                <span className="ts-row__name" title={i.label}>
                  {name}
                </span>
                <div className="ts-row__controls">
                  <Button
                    type="text"
                    size="small"
                    className="ts-row__eye"
                    aria-label={`Hide ${name}`}
                    aria-pressed={off}
                    title={off ? "Hidden: click to show" : "Hide"}
                    icon={<FontAwesomeIcon icon={off ? faEyeSlash : faEye} />}
                    onClick={() => toggle(i.id)}
                  />
                </div>
              </li>
            )
          })}
        </ol>
      </Field>
      <RowOrder tableId={tableId} rows="grid rows" sortable fromData={q.source === "data"} />
      <AddNetModal
        open={netOpen}
        title="A net of answers"
        options={answers.filter((a) => !q.options.find((o) => o.label === a.value)?.exception)}
        onCancel={() => setNetOpen(false)}
        onConfirm={(name, members) => {
          if (!name.trim()) return "A net needs a name."
          if (members.length === 0) return "Choose at least one answer."
          writeSummary({ kind: "net", name: name.trim(), members })
          setNetOpen(false)
          return null
        }}
      />
    </div>
  )
}

export default GridSummaryRows
